import { DataTable } from '@/components/features/shared/DataTable'
import { PriceLabelSimple, renderDatetime } from '@/components/features/shared/utils'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import {
  AgreementStatus,
  CremationData,
  formatAgreementStatus,
  getAgreementStatusColor
} from '@/domain/agreements/agreement'
import { useDataCremations } from '@/hooks/api/useAgreements'
import { useServerPagination } from '@/hooks/useServerPagination'
import { Paths } from '@/routes/paths'
import { formatDocNumber } from '@/utils/helpers'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import { addMonths, endOfMonth, format, startOfMonth, subMonths } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search
} from 'lucide-react'
import { PageContainer } from '@/components/features/shared/PageContainer'
import { NavHeader } from '@/components/features/shared/NavHeader'

export const CremationsManagement = () => {
  const navigate = useNavigate()
  const searchParams = useSearch({ strict: false }) as any

  const statusFilter = searchParams.status || 'all'
  const monthParam = searchParams.month // YYYY-MM
  const showByMonth = !!monthParam

  const selectedMonth = monthParam ? new Date(monthParam + '-01T12:00:00') : new Date()

  const {
    pagination,
    setPagination: handlePaginationChange,
    search,
    inputValue,
    setInputValue,
    handleSearch,
    updateQueryParams,
    limit,
  } = useServerPagination({
    clearDefaults: {
      status: 'all',
    },
    resetOn: [statusFilter],
  })

  const { data: subscriptions, isLoading } = useDataCremations({
    search,
    status: statusFilter === 'all' ? undefined : statusFilter,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    startDate: showByMonth ? format(startOfMonth(selectedMonth), 'yyyy-MM-dd') : undefined,
    endDate: showByMonth ? format(endOfMonth(selectedMonth), 'yyyy-MM-dd') : undefined,
  })

  const handleEdit = (agreementId: string) => {
    navigate({
      to: `${Paths.Management.Contracts}/${agreementId}`,
    })
  }

  const handlePrevMonth = () => {
    const newMonth = subMonths(selectedMonth, 1)
    updateQueryParams({ month: format(newMonth, 'yyyy-MM') })
  }

  const handleNextMonth = () => {
    const newMonth = addMonths(selectedMonth, 1)
    updateQueryParams({ month: format(newMonth, 'yyyy-MM') })
  }

  const handleCurrentMonth = () => {
    updateQueryParams({ month: format(new Date(), 'yyyy-MM') })
  }

  const columns: ColumnDef<CremationData>[] = [
    {
      accessorKey: 'contractNumber',
      header: 'Contrato',
      cell: ({ row }) => {
        const cremation = row.original
        return (
          <div className="flex flex-col">
            <span className="text-sm font-medium">{cremation.contractNumber}</span>
            <span className="text-xs text-muted-foreground">{cremation.offeringName}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'userName',
      header: 'Cliente',
      cell: ({ row }) => {
        const cremation = row.original
        return (
          <div className="font-medium whitespace-nowrap flex flex-col">
            {cremation.userName}
            <span className="text-xs text-muted-foreground">{cremation.userDocType} {formatDocNumber(cremation.userDocNumber, cremation.userDocType)}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ row }) => {
        const cremation = row.original
        return (
          <Badge className={getAgreementStatusColor(cremation.status)}>
            {formatAgreementStatus(cremation.status)}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'sellCode',
      header: 'Plan',
      cell: ({ row }) => {
        const cremation = row.original
        return (
          <div className="flex items-center justify-center">
            <span className='text-xs font-bold uppercase text-muted-foreground'>{cremation.sellCode ?? '-'}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'deceasedName',
      header: ({ column }) => (
        <span className="text-sm font-semibold">Fallecido</span>
      ),
      cell: ({ row }) => {
        const cremation = row.original
        return (
          <span className='text-sm font-medium text-muted-foreground'>{cremation.deceasedName}</span>
        )
      },
    },
    {
      accessorKey: 'startDate',
      header: 'Fecha',
      cell: ({ row }) => {
        const cremation = row.original
        return (
          <span className="whitespace-nowrap flex flex-col">
            <span className='text-sm'>{format(cremation.startDate, 'dd/MM/yyyy')}</span>
          </span>
        )
      },
    },
    {
      accessorKey: 'totalAmount',
      header: 'Importe Total',
      cell: ({ row }) => {
        const cremation = row.original
        return (
          <div className="font-medium">
            <PriceLabelSimple price={cremation.totalAmount} />
          </div>
        )
      },
    },
    {
      accessorKey: 'totalPaidAmount',
      header: 'Pagado',
      cell: ({ row }) => {
        const cremation = row.original
        return (
          <div className="font-medium w-[max-content]">
            <PriceLabelSimple price={cremation.totalPaidAmount} />
          </div>
        )
      },
    },
    {
      accessorKey: 'totalOutstandingAmount',
      header: 'Deuda',
      cell: ({ row }) => {
        const cremation = row.original
        return (
          <div className={`font-bold w-[max-content] ${cremation.totalOutstandingAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
            <PriceLabelSimple price={cremation.totalOutstandingAmount} />
          </div>
        )
      },
    },
    {
      accessorKey: 'updatedAt',
      header: 'Ult. modificación',
      cell: ({ row }) => {
        const cremation = row.original
        return renderDatetime(cremation.updatedAt ?? null)
      },
    },
    {
      id: 'actions',
      header: 'Acciones',
      cell: ({ row }) => {
        const cremation = row.original
        return (
          <div className="text-right">
            <IconButton
              title='Editar contrato'
              onClick={(e) => {
                e.stopPropagation()
                handleEdit(cremation.id)
              }}
            >
              <ArrowRight className="h-3.5 w-3.5" />
            </IconButton>
          </div>
        )
      },
    },
  ]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <PageContainer>
      <NavHeader hideBackButton>
        <h1 className="text-3xl font-bold tracking-tight">Cremaciones</h1>
        <p className="text-muted-foreground">
          Gestión de contratos de cremación
        </p>
      </NavHeader>

      <div className="flex flex-col gap-4">
        {/* Month Navigation */}
        <div className="flex items-center justify-between bg-muted/30 p-4 rounded-lg border">
          <div className="flex items-center space-x-2">
            <Switch
              id="show-by-month"
              checked={showByMonth}
              onCheckedChange={(checked) => {
                updateQueryParams({
                  month: checked ? (monthParam || format(new Date(), 'yyyy-MM')) : undefined,
                  page: 1
                })
              }}
            />
            <Label htmlFor="show-by-month">Ver contratos por mes</Label>
          </div>

          {showByMonth && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePrevMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-[150px] text-center font-medium capitalize">
                {format(selectedMonth, 'MMMM yyyy', { locale: es })}
              </div>
              <Button variant="outline" size="sm" onClick={handleNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleCurrentMonth} className="ml-2">
                Mes actual
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-end gap-4">
          <div className="flex flex-col gap-y-2 w-full max-w-sm">
            <Label>Búsqueda</Label>
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por cliente o contrato..."
                className="pl-8"
                autoFocus
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch()
                  }
                }}
              />
            </div>
          </div>
          <div className="flex flex-col gap-y-2">
            <Label>Estado</Label>
            <Select value={statusFilter} onValueChange={(val) => {
              updateQueryParams({ status: val })
            }}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value={AgreementStatus.Pending}>Pendiente</SelectItem>
                {/* <SelectItem value={AgreementStatus.Active}>Activo</SelectItem> */}
                {/* <SelectItem value={AgreementStatus.Suspended}>Suspendido</SelectItem> */}
                <SelectItem value={AgreementStatus.Terminated}>Completado</SelectItem>
                {/* <SelectItem value={AgreementStatus.Expired}>Expirado</SelectItem> */}
              </SelectContent>
            </Select>
          </div>
          <Button className='ml-auto' onClick={handleSearch} variant="info">Buscar</Button>
        </div>
      </div>

      <Card>
        <CardHeader className='pb-2'>
          <CardTitle>Lista de Cremaciones</CardTitle>
          <CardDescription>
            Contratos de servicio de cremación
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={subscriptions?.data || []}
            pageCount={subscriptions?.totalPages || -1}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
            totalRecords={subscriptions?.total}
            onRowClick={(cremation) => handleEdit(cremation.id)}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
